// Paket AA, Schritt AA-10 (Befund 9): kurze Antworten zu häufigen Aufgaben im Bereich „Hilfe“. Die Texte beschreiben nur,
// was der Code heute tut. Jede genannte Beschriftung steht in belege mit der Datei, die sie zeigt (Pfad ab Repo-Wurzel);
// fakten belegen Zahlen und Regeln des Servers mit einem Muster. Der Wächter frontend/tests/hilfe-texte.test.mjs prüft
// beides: benennt jemand einen Knopf um, wird er rot und die Hilfe wird nachgezogen.
// Plan 1a Welle 1, G3-8 (welle-1/AUFTEILUNG.md 8.2): alle Themen nach der Wortliste (Abschnitt 2) und den Texten der Gruppen
// G1 und G2; neu das Thema „Was bedeutet …?“ (9.01 bis 9.06, M1-34). Wörter, die eine Beschriftung nur zur Laufzeit bilden
// (Platzbegriff der Firma), belegen fakten.
// Plan 1a Welle 2, Schluss der Runde (SPEC-GEMEINSAM.md GW-03 Soll 3; SPEC-ADMIN.md GZ-5, GZ-10, GZ-17; welle-2/UEBERGABE.md
// US-G11, AD-20): Hilfe-Nachzug nach dem Zusammenführen der vier Spuren. Jedes Thema nennt wieder die Knöpfe, Abschnitte und
// Sätze, die die Seiten seit Welle 2 zeigen; Wörter, die es dort nicht mehr gibt, stehen hier nicht mehr.
import type { Bereich, FirmendatenAnsicht } from './seiten';
import { rollenErklaerung } from '../../administration/rollenWort';
import { GELD } from '../../administration/billing/geldWoerter';
import { GUTHABEN_TEXTE } from '../../administration/billing/guthabenTexte';
import { EBENEN as EBENEN_WOERTER, MENUE } from '../../config/ebenen';
import { GUTE_FRAGE_ID, RECHERCHE_TIPPS } from '../rechercheTipps';

export type HilfeThema = {
  id: string;
  frage: string;
  // Firmenthema: sichtbar, wenn die Gruppe „firma“ der Navigation diesen Bereich zeigt; null: jedes Konto.
  bereich: Bereich | null;
  // Schlusslauf 4 (M-2; GW-03, G-05): eine eigene Ansicht von „Firmendaten“ (seiten.ts FIRMEN_ANSICHTEN); der Weg „Zu …“ führt
  // dann genau dorthin (…/firmendaten#besitz bzw. #sicherheit-der-firma) und nennt sie, nicht „Firmendaten“.
  ansicht?: Exclude<FirmendatenAnsicht, 'firmendaten'>;
  // Profil-Nachbau F-23 (PF-NB-hilfe-1, Z-04): Gruppe auf „Dokumente“; ohne Angabe gruppeVon (Bereich → verwaltung, sonst begriffe).
  gruppe?: HilfeGruppe;
  // Profil-Nachbau F-23 (PF-NB-hilfe-1, Z-01): weitere Wörter, unter denen die Suchen das Thema finden; nur Oberfläche.
  stichwoerter?: string[];
  schritte: string[];
  hinweise: string[];
  belege: { datei: string; text: string }[];
  fakten?: { datei: string; muster: string }[];
};

// Profil-Nachbau F-23 (PF-NB-hilfe-1; Nutzerblick Mitglied Z-04): „Dokumente“ beginnt mit dem, was jede Person selbst tut, und
// stellt Verwaltungsthemen ans Ende (meinGPT: eigene Admin-Spur „nur für Admins“). Die Reihenfolge der Liste bleibt für Pop-up
// und Hilfe-Assistent.
export type HilfeGruppe = 'recherche' | 'konto' | 'begriffe' | 'verwaltung';
export const HILFE_GRUPPEN_REIHE: readonly HilfeGruppe[] = ['recherche', 'konto', 'begriffe', 'verwaltung'];
export const gruppeVon = (thema: Pick<HilfeThema, 'gruppe' | 'bereich'>): HilfeGruppe => thema.gruppe ?? (thema.bereich ? 'verwaltung' : 'begriffe');

const SEITEN = 'frontend/src/pages/einstellungen/seiten.ts';
const ADMINISTRATION = 'frontend/src/administration/Administration.tsx';
const FORMULARE = 'frontend/src/administration/forms.tsx';
const BEENDEN = 'frontend/src/administration/memberRemoval.ts';
const EINLADUNGEN = 'frontend/src/administration/Invitations.tsx';
const EINLADUNG_TEXTE = 'frontend/src/administration/einladungTexte.ts';
const ABRECHNUNG = 'frontend/src/administration/billing/BillingPanel.tsx';
const BETRAEGE = 'frontend/src/administration/billing/shared.tsx';
const BELEG = 'frontend/src/administration/billing/BelegAnsicht.tsx';
const GELD_WOERTER = 'frontend/src/administration/billing/geldWoerter.ts';
const VERTRAG = 'frontend/src/administration/billing/LegalConsent.tsx';
const TEAM_NUTZUNG = 'frontend/src/administration/organization/TeamNutzung.tsx';
const FIRMENDATEN = 'frontend/src/administration/organization/Firmendaten.tsx';
const PROTOKOLL = 'frontend/src/administration/protokollZeile.ts';
const EBENEN = 'frontend/src/config/ebenen.ts';
const UNTERSEITEN = 'frontend/src/pages/einstellungen/Unterseiten.tsx';
const ZWEI_FAKTOR_ABSCHNITT = 'frontend/src/components/account/ZweiFaktorAbschnitt.tsx';
const ZWEI_FAKTOR_EINRICHTEN = 'frontend/src/components/account/ZweiFaktorEinrichten.tsx';
const ZWEI_FAKTOR_TEXTE_DATEI = 'frontend/src/components/account/zweiFaktor.ts';
const DIENST = 'backend/src/administration/service.ts';
const KUNDEN = 'backend/src/administration/customers.ts';
const KATALOG = 'backend/src/administration/capabilities.ts';
const TEAM_DIENST = 'backend/src/administration/organization/teamUsage.ts';
const FIRMA_DIENST = 'backend/src/administration/organization/company.ts';
const REGEL_DIENST = 'backend/src/administration/organization/loginPolicy.ts';
const PFLICHT = 'backend/src/administration/twoFactor/policy.ts';
const ZWEI_FAKTOR_DIENST = 'backend/src/administration/twoFactor/service.ts';
// Admin-Wünsche W6, W7 (Schritt M-4): Rollen und Pause. Die Menüeinträge „Zum … machen …“ entstehen aus den Begriffen der
// Organisation (mitgliedTexte.ts); belegt wird deshalb die Vorlage (fakten), die Vorgaben der Begriffe stehen in BrandContext.
const ROLLEN = 'frontend/src/administration/Roles.tsx';
const MITGLIED_TEXTE = 'frontend/src/administration/mitgliedTexte.ts';
const MITGLIED_AKTIONEN = 'frontend/src/administration/MitgliedAktionen.tsx';
const BAUSTEINE = 'frontend/src/pages/einstellungen/Bausteine.tsx';
const BEGRIFFE = 'frontend/src/contexts/BrandContext.tsx';
const MAIL = 'backend/src/administration/communication/mail.ts';
const TARIF_WECHSEL = 'frontend/src/administration/billing/TarifWechsel.tsx';
const TARIF_UND_KOSTEN = 'frontend/src/administration/billing/TarifUndKosten.tsx';
// Plan 1a Welle 2: die Wörter der Spur admin stehen in eigenen Textdateien; die Hilfe zitiert sie von dort.
const VORSCHAU_TEXTE = 'frontend/src/administration/billing/vorschauTexte.ts';
const ALLE_BELEGE = 'frontend/src/administration/billing/AlleBelege.tsx';
const BESITZ_TEXTE_DATEI = 'frontend/src/administration/organization/besitzTexte.ts';
const SICHERHEIT_TEXTE_DATEI = 'frontend/src/administration/organization/sicherheitTexte.ts';
const ROLLEN_SEITE_TEXTE = 'frontend/src/administration/rollenSeiteTexte.ts';
const TEAM_NUTZUNG_TEXTE = 'frontend/src/administration/organization/teamNutzung.ts';
// Plan 1a MA-02: die eine Erklärung der drei Rollen steht in rollenWort.ts; die Hilfe zeigt sie mit den Standardbegriffen
// (BrandContext), die Mitgliederliste mit den Begriffen der Firma (MK-1c).
const ROLLEN_WORT = 'frontend/src/administration/rollenWort.ts';
const PROFIL_ZEILEN = 'frontend/src/pages/einstellungen/profilZeilen.ts';
// Spur guthaben (GU-31, GU-47): die Wörter zum zusätzlichen Guthaben und zum Token stehen in billing/guthabenTexte.ts.
const GUTHABEN_TEXTE_DATEI = 'frontend/src/administration/billing/guthabenTexte.ts';
// Spur nachbesserung-m3, G-1: die Rechnungsanschrift unter „Firmendaten“ mit dem Link zum Rechnungsprofil.
const RECHNUNGSANSCHRIFT_DATEI = 'frontend/src/administration/organization/rechnungsanschrift.ts';
// Profil-Nachbau F-08 (PF-508): die Wortlaute des KI-Cockpits; das Wort „KI-Cockpit“ selbst steht in config/ebenen.ts (EBENEN).
const COCKPIT_TEXTE = 'frontend/src/administration/cockpit/cockpitTexte.ts';
const COCKPIT_FIRMA = 'backend/src/administration/cockpit/firma.ts';
// Profil-Nachbau F-06 (PF-810): die Wortlaute der Teams, der Vorlagen, von „Verträge & AVV“, der Nutzungsrichtlinie und des
// Änderungsprotokolls; die Einträge der Leiste „Einstellungen der Firma“ stehen in firmenLeiste.ts.
const FIRMEN_LEISTE = 'frontend/src/administration/organization/firmenLeiste.ts';
const TEAM_TEXTE = 'frontend/src/administration/teams/teamTexte.ts';
const VORLAGEN_TEXTE = 'frontend/src/pages/vorlagen/vorlagenTexte.ts';
const AVV_TEXTE = 'frontend/src/administration/recht/avvTexte.ts';
const RICHTLINIE_TEXTE = 'frontend/src/administration/recht/richtlinieTexte.ts';
const FIRMEN_PROTOKOLL = 'frontend/src/administration/FirmenProtokoll.tsx';
const PROTOKOLL_TEXTE = 'frontend/src/administration/protokoll/protokollTexte.ts';
const TEAMS_MIGRATION = 'backend/prisma/migrations/20261014163000_teams/migration.sql';
const KONTINGENT_ZAEHLUNG = 'backend/src/administration/billing/usage.ts';
const RECHERCHE_WAECHTER = 'backend/src/services/RISearch_tooluse/QueryGuard.ts';
const RECHERCHE_ZUGANG = 'backend/src/administration/researchUsage.ts';
const ANMELDUNG_PRUEFUNG = 'backend/src/middleware/auth.ts';
const PROTOKOLL_KLASSEN = 'backend/src/administration/protokoll/klassen.ts';
const STANDARD_BEGRIFFE = { adminLabel: 'Administrator', memberLabel: 'Mitglied' };
const DASHBOARD = 'frontend/src/pages/Dashboard.tsx';
const RECHERCHE_TIPPS_DATEI = 'frontend/src/pages/rechercheTipps.ts';
const ANTWORT_BEWERTUNG = 'frontend/src/components/AnswerRating.tsx';
const TICKET_TEXTE_DATEI = 'frontend/src/components/hilfe/ticketTexte.ts';
const HILFE_TEXTE_DATEI = 'frontend/src/components/hilfe/hilfeTexte.ts';
// Der Platzbegriff einer Firma kommt aus der Marke (MK-1c); im Formular steht „{Platzbegriff} zuweisen“ bzw. „… zugewiesen“.
const PLATZ_FELD = { datei: FORMULARE, muster: '\\$\\{platzEinzahl\\(brand\\.seatLabel\\)\\} zuweisen' };

// Mehrere Belege aus einer Datei.
const aus = (datei: string, ...texte: string[]) => texte.map(text => ({ datei, text }));

export const HILFE_THEMEN: HilfeThema[] = [
  {
    // Plan 1a MA-02 (Zitate 4.01 bis 4.03, Befund S-02): die eine Erklärung, wer was darf; jedes Konto sieht sie, das Profil
    // führt mit „Wer darf was?“ hierher.
    id: 'rollen-woerter',
    frage: 'Wer darf was: Besitzer, Administrator, Mitglied?',
    bereich: null,
    gruppe: 'konto',
    schritte: rollenErklaerung(STANDARD_BEGRIFFE),
    hinweise: [
      // Plan 1a Welle 2, Spur user (UQ-03): der erste Hinweis erklärt das Wort „Zusatzrolle“, das die Sätze darüber benutzen
      // (Erika: „‚Zusatzrolle‘ verstehe ich nicht“); er steht direkt unter den drei Rollensätzen.
      // Spur kontingent, KO-35 (SPEC-KONTINGENT.md KT-73, 2.3): „Abrechnung und Kontingent“ statt „Abrechnung und Kosten“.
      'Zusatzrolle heißt: Die Firma hat einer einzelnen Person Rechte weggenommen oder dazugegeben. Ein eingeschränkter Administrator verwaltet weiter Mitglieder und Plätze; der Besitzer behält immer Abrechnung und Kontingent.',
      'Ihre eigene Rolle steht unter „Profil“ in der Zeile „Rolle“.',
    ],
    belege: [
      // Plan 1a Welle 1 (G2-0, W1-05, 1A-5): die Erklärung ohne Sichtwechsel für Firmenrollen.
      ...aus(ROLLEN_WORT, 'genau eine Person je Firma. Hat alle Rechte der Rolle',
        'und immer Abrechnung und Kontingent; nur der Besitzer kann die Firma an eine andere Person übergeben.',
        ': verwaltet Mitglieder, Einladungen, Plätze und Rollen und dazu Abrechnung, Rechnungen und Kontingent – außer eine Zusatzrolle schränkt das ein.',
        // Plan 1a Welle 2 (Ä-09 Punkt 5, UK-04): der neue Satz des Mitglieds, in zwei Belegen (dazwischen wählt rollenWort.ts
        // nach dem Begriff der Firma für den Administrator).
        ': recherchiert mit Platz und ändert die eigenen Einstellungen (Name, E-Mail-Adresse, Passwort, Zwei-Faktor-Anmeldung).',
        'Leute hinzufügen, Plätze und Rechnungen verwalten Besitzer und Administratoren.', 'Besitzer'),
      ...aus(BEGRIFFE, 'Administrator', 'Mitglied'),
      ...aus(SEITEN, 'Profil'),
      ...aus(PROFIL_ZEILEN, 'Rolle'),
    ],
    fakten: [
      { datei: ROLLEN_WORT, muster: 'Rolle „\\$\\{b\\.adminLabel\\}“' },
      { datei: ROLLEN_WORT, muster: '`\\$\\{b\\.memberLabel\\}: recherchiert' },
      { datei: KUNDEN, muster: 'OWNER_FLOOR' },
    ],
  },
  {
    // Profil-Nachbau F-23 (PF-NB-hilfe-1; Nutzerblick Mitglied Z-01, Schwere 3: beide Personas fanden keine Hilfe zum Fragenstellen):
    // die Anleitung zum Formulieren, nach meinGPT „Klare Prompts schreiben“ (Lernpfad Basis-Wissen). Die Startseite verweist mit
    // „So stellen Sie eine gute Rechtsfrage“ hierher (#hilfe-gute-frage); die Tipps stehen in pages/rechercheTipps.ts.
    id: GUTE_FRAGE_ID,
    frage: 'Wie stelle ich eine gute Rechtsfrage?',
    bereich: null,
    gruppe: 'recherche',
    stichwoerter: ['Frage stellen', 'gute Frage', 'Rechtsfrage stellen', 'formulieren', 'Recherchetipps', 'Tipps', 'Prompt', 'Sachverhalt', 'Fundstelle', 'Beispielfrage'],
    schritte: [
      'Schreiben Sie den Sachverhalt in ganzen Sätzen in das Feld „Ihre Rechtsfrage“: wer beteiligt ist, was passiert ist und seit wann. Namen und Mandantendaten lassen Sie weg; „ein Angestellter“ oder „eine GmbH“ genügt.',
      'Stellen Sie am Ende die konkrete Frage, etwa „Ist die Kündigung fristgerecht?“ statt nur „Kündigung“.',
      'Grenzen Sie ein, was für die Antwort zählt: den Zeitpunkt (es gilt das Recht zu diesem Datum), das Bundesland und die Branche bzw. den Kollektivvertrag.',
      `Klicken Sie „Recherche starten“. Vertiefen Sie danach im selben Chat: ${RECHERCHE_TIPPS[2].satz}`,
      `${RECHERCHE_TIPPS[1].titel}: ${RECHERCHE_TIPPS[1].satz}`,
    ],
    hinweise: [
      'Beispiel: Statt „Überstunden Handel“ besser: „Eine Angestellte im Handel arbeitet seit März 2026 regelmäßig 45 Stunden pro Woche. Welche Zuschläge sieht der Kollektivvertrag für Handelsangestellte vor, und darf der Arbeitgeber stattdessen Zeitausgleich geben?“',
      'Fertige Beispielfragen finden Sie auf der Startseite unter „Mit einem Beispiel beginnen“. Fragen, die Sie öfter stellen, speichern Sie als Vorlage; „Vorlage einfügen“ am Eingabefeld setzt sie ein.',
      'Fundstelle heißt: die Stelle in einem Gesetz, einer Entscheidung oder einem Kollektivvertrag, auf die sich die Antwort stützt. Ein Klick darauf öffnet sie.',
      'CLAiRIS recherchiert, verfasst aber keine Schriftstücke. Eine Frage nach einem Brief, einem Vertrag oder einem Mustertext lehnt die Recherche ab.',
    ],
    belege: [
      ...aus(DASHBOARD, 'Ihre Rechtsfrage', 'Recherche starten', 'Mit einem Beispiel beginnen'),
      ...aus(RECHERCHE_TIPPS_DATEI, RECHERCHE_TIPPS[1].titel, RECHERCHE_TIPPS[1].satz, RECHERCHE_TIPPS[2].satz),
      ...aus(VORLAGEN_TEXTE, 'Vorlage einfügen'),
    ],
    fakten: [
      { datei: RECHERCHE_WAECHTER, muster: 'Mustertext' },
    ],
  },
  {
    // Profil-Nachbau F-23 (PF-NB-hilfe-3; Nutzerblick Firma Z-04: drei von drei Personas fanden keinen Weg, eine falsche Antwort zu
    // melden): Bewertung an der Antwort, dann „Dem Support melden“ mit der Kategorie ANTWORT_FALSCH; die Recherche nur mit Haken.
    id: 'antwort-melden',
    frage: 'Eine falsche Antwort melden',
    bereich: null,
    gruppe: 'recherche',
    stichwoerter: ['falsche Antwort', 'Antwort melden', 'Fehler in der Antwort', 'Daumen', 'Bewertung'],
    schritte: [
      'Klicken Sie unter der Antwort auf den Daumen nach unten („Antwort nicht hilfreich“) und wählen Sie bei „Was stimmt nicht?“ den Grund. Diese Bewertung sieht Ihre Firma nicht.',
      'Soll sich der Support die Antwort ansehen, klicken Sie danach auf „Dem Support melden“. Im Formular ist „Falsche oder zweifelhafte Antwort“ schon gewählt.',
      'Mit dem Haken „Frage und Antwort dieser Recherche mitschicken“ sieht der Support Ihre Frage und die Antwort. Ohne Haken schickt CLAiRIS keine Inhalte Ihrer Recherche mit.',
    ],
    hinweise: [
      'Bitte setzen Sie den Haken nur, wenn in Frage und Antwort keine Mandantendaten stehen.',
      'Eine Antwort ohne Daumen melden Sie unter „Tickets“ mit „Neue Anfrage“ und „Falsche oder zweifelhafte Antwort“.',
    ],
    belege: [
      ...aus(ANTWORT_BEWERTUNG, 'Antwort nicht hilfreich', 'Was stimmt nicht?'),
      ...aus(TICKET_TEXTE_DATEI, 'Dem Support melden', 'Falsche oder zweifelhafte Antwort', 'Frage und Antwort dieser Recherche mitschicken', 'Neue Anfrage'),
      ...aus(HILFE_TEXTE_DATEI, 'Tickets'),
    ],
    fakten: [
      { datei: 'backend/src/support/tickets.ts', muster: "rechercheAnhaengen === true" },
      { datei: 'backend/src/support/regeln.ts', muster: "'ANTWORT_FALSCH'" },
    ],
  },
  {
    // Plan 1a Welle 1, G3-8 (9.01 bis 9.06 „Zusatzrolle, Plattformrolle, Organisationsrolle – drei Wörter“, M1-34 „ich verstehe
    // nur ein Wort nicht“): je Wort ein Satz. Die Einstellungssuche führt ohne Treffer hierher (…/hilfe#hilfe-woerter).
    id: 'woerter',
    frage: 'Was bedeutet …?',
    bereich: null,
    schritte: [
      // Spur kontingent, KO-35 (SPEC-KONTINGENT.md KT-70 bis KT-72): Kontingent, Zusatzkontingent, Verbrauch über dem Kontingent
      // und Prozent, wörtlich; kein Betrag in Euro.
      `${GELD.kontingent}: die Menge an KI-Nutzung, die im Preis Ihres Tarifs enthalten ist. Die Seiten zeigen in Prozent, wie viel davon verwendet ist. Im Preis enthalten. Beginnt jeden Monat neu; ein Rest verfällt.`,
      // Spur guthaben, GU-31 (SPEC-KONTINGENT.md 14.1; welle-2/guthaben/AUFTEILUNG.md W-S12, PE-12): das zusätzliche Guthaben lädt
      // die Firma selbst auf; Zusatzkontingent gibt nur noch der Anbieter dazu (keine Stufen, kein Kauf).
      GUTHABEN_TEXTE.hilfeGuthaben,
      // Spur nachbesserung-m3, K-9 (B-ludwig P-1): wo „Automatisch aufladen“ eingestellt wird und welche zwei Wege dorthin führen.
      `${GUTHABEN_TEXTE.autoAufladen}: lädt das zusätzliche Guthaben von selbst nach, wenn es unter einen Betrag fällt, den Ihre Firma wählt. Eingestellt wird es unter „Kontingent“ › „Zusätzliches Guthaben“, von allen, die Guthaben aufladen dürfen. Dorthin führen auf „Tarif und Kosten“ der Link „Einstellen unter „Kontingent“ › „Zusätzliches Guthaben““ und im Fenster „Guthaben aufladen“ der Link „Automatisch aufladen“.`,
      GUTHABEN_TEXTE.hilfeZusatzkontingent,
      'Verbrauch über dem Kontingent: was der Anbieter von CLAiRIS Ihrer Firma über das Kontingent hinaus erlaubt hat. Es ist kein Zusatzkontingent und gilt, bis der Anbieter es ändert.',
      'Prozent: Ein Prozent ist ein Hundertstel des Kontingents Ihrer Firma. Es steht für keinen festen Geldbetrag und keine feste Zahl an Recherchen: Eine aufwendige Recherche verwendet mehr als eine kurze.',
      `${GELD.verwendet}: ${GELD.verbrauchSatz}`,
      `Recherchen im Monat: Für Alttarife gilt: ${GELD.monatsSatz}`,
      `Kalendermonat: ${GELD.monatsbeginnSatz}`,
      'Lizenzplatz: ein Platz Ihres Tarifs. Ohne Platz kann die Person beitreten, aber nicht recherchieren. Verwalten geht auch ohne Platz.',
      'Rolle: Ihre Stellung in der Firma, Besitzer, Administrator oder Mitglied (siehe „Wer darf was“).',
      // Plan 1a Welle 2 (Ä-03 Punkt 3, US-A2): der Satz der Rollen-Seite, ohne „Leserechte“. F-24 (PF-TX-firma-2): kürzer wie dort.
      'Zusatzrolle: Zusatzrollen nehmen Rechte weg oder geben einzelne dazu.',
      'Besitzer: genau eine Person je Firma; nur der Besitzer kann die Firma an eine andere Person übergeben.',
      `${GELD.anbieter}: Eine Person beim Anbieter hat die Änderung vorgenommen, etwa bei der Einrichtung oder auf Ihre Bitte. So steht es im Änderungsprotokoll.`,
      `${GELD.sso}: Sie melden sich mit dem Konto Ihrer Firma an, ohne eigenes Passwort bei CLAiRIS.`,
      'Authenticator-App: Der zweite Schritt der Anmeldung nutzt einen Code aus einer kostenlosen App am Handy; sie zeigt alle 30 Sekunden einen neuen sechsstelligen Code, etwa Microsoft Authenticator oder Google Authenticator.',
      'Wiederherstellungscode: Mit einem Code melden Sie sich an, wenn das Handy fehlt. Jeder Code gilt einmal.',
      'Angemeldete Geräte: die Browser, in denen Ihr Konto gerade angemeldet ist; unter „Sicherheit“ melden Sie ein Gerät ab.',
      // Plan 1a Welle 2 (GW-03, Ä-35 Punkt 3): die Hilfe sagt an allen drei Stellen, was im echten Betrieb geschieht.
      'Vorführumgebung: eine Umgebung zum Ausprobieren. Dort steht „Vorführumgebung: E-Mails werden nur zur Ansicht vorbereitet und nicht verschickt.“, und Belege heißen „Musterbeleg“. Im echten Betrieb schickt CLAiRIS die Einladung selbst per E-Mail, und nach jeder Zahlung steht ein Beleg da.',
      'Musterbeleg: So steht es auf jedem Beleg der Vorführumgebung: „Musterbeleg aus der Vorführumgebung – keine Rechnung, es wurde nichts abgebucht.“',
      'Als Tabelle herunterladen (für Excel): speichert die Zahlen als Datei, die Excel öffnet.',
    ],
    hinweise: [
      // Plan 1a Welle 2 (Ä-32): am Handy steht die Suche über der Liste der Einstellungen; derselbe Satz wie im Kopf der Hilfe.
      'Ein Wort gesucht? Die Suche über der Liste der Einstellungen findet auch Begriffe.',
    ],
    belege: [
      ...aus(GUTHABEN_TEXTE_DATEI, GUTHABEN_TEXTE.hilfeGuthaben, GUTHABEN_TEXTE.hilfeZusatzkontingent, 'Automatisch aufladen',
        'Einstellen unter „Kontingent“ › „Zusätzliches Guthaben“', 'Guthaben aufladen'),
      ...aus(SEITEN, 'Kontingent', 'Tarif und Kosten'),
      // „Wird extra in Rechnung gestellt.“ steht seit KO-35 in keinem Satz mehr; seit GU-31 sagt es GUTHABEN_TEXTE.hilfeGuthaben (oben belegt).
      ...aus(GELD_WOERTER, 'Kontingent', 'Zusatzkontingent', 'Vom Kontingent verwendet',
        // Schlusslauf 1 (Befund 13:20): der Satz heißt seit PF-752b „Ein neuer Kalendermonat beginnt am 1. um 0 Uhr …“; belegt wird
        // nur der Anfang (die Ratsche O2-3 lässt „0 Uhr“ nur in geldWoerter.ts zu).
        'kostet nichts extra', 'Recherchen im Monat zählen gestartete Recherchen', 'Ein neuer Kalendermonat beginnt am 1.',
        'Anbieter von CLAiRIS', 'Anmeldungen über Ihr Firmenkonto (SSO)', 'Als Tabelle herunterladen (für Excel)'),
      ...aus(EINLADUNG_TEXTE, 'Ohne Platz kann die Person beitreten, aber nicht recherchieren.'),
      ...aus(ROLLEN, 'Zusatzrollen nehmen Rechte weg oder geben einzelne dazu.'),
      ...aus(ROLLEN_WORT, 'nur der Besitzer kann die Firma an eine andere Person übergeben.'),
      ...aus(PROTOKOLL, 'Eine Person beim Anbieter hat die Änderung vorgenommen, etwa bei der Einrichtung oder auf Ihre Bitte.'),
      ...aus(ZWEI_FAKTOR_TEXTE_DATEI, 'kostenlosen App am Handy', 'Mit einem Code melden Sie sich an, wenn das Handy fehlt.'),
      ...aus(UNTERSEITEN, 'Angemeldete Geräte'),
      ...aus(SEITEN, 'Sicherheit'),
      ...aus(EINLADUNGEN, 'Vorführumgebung: E-Mails werden nur zur Ansicht vorbereitet und nicht verschickt.'),
      ...aus(BELEG, 'Musterbeleg aus der Vorführumgebung – keine Rechnung, es wurde nichts abgebucht.'),
    ],
    fakten: [
      { datei: 'backend/src/utils/totp.ts', muster: 'TOTP_STEP_SECONDS = 30' },
    ],
  },
  {
    // Spur guthaben, GU-47 (SPEC-KONTINGENT.md KO-E4): das Hilfe-Thema „Was ist ein Token?“ in einem Satz, wörtlich. Jedes Konto
    // sieht es; die Einstellungssuche findet es mit „Token“, „Guthaben“ und „aufladen“ (seiten.ts, Anker hilfe-token).
    id: 'token',
    frage: GUTHABEN_TEXTE.tokenHilfeTitel,
    bereich: null,
    schritte: [GUTHABEN_TEXTE.tokenHilfe],
    hinweise: [],
    belege: [...aus(GUTHABEN_TEXTE_DATEI, GUTHABEN_TEXTE.tokenHilfe, GUTHABEN_TEXTE.tokenHilfeTitel)],
  },
  {
    id: 'einladen',
    frage: 'Person einladen',
    // Plan 1a Welle 1 (MA-07): „Einladungen“ ist ein Abschnitt in „Mitglieder“; das Thema hängt an diesem Bereich.
    bereich: 'members',
    schritte: [
      'Öffnen Sie „Mitglieder“ und wählen Sie „Person einladen“. Offene Einladungen stehen darunter im Abschnitt „Einladungen“.',
      // Plan 1a MA-02 (W-12): die Rollenwahl nennt die Begriffe der Firma; belegt über die Vorgaben und die Vorlage (fakten).
      'Geben Sie die E-Mail-Adresse ein und wählen Sie die Rolle: „Mitglied“ oder, wenn Sie Rollen vergeben dürfen, „Administrator“.',
      'Lassen Sie den Haken beim Lizenzplatz gesetzt, wenn die Person gleich recherchieren soll. Ohne Platz kann die Person beitreten, aber nicht recherchieren. Den Platz können Sie auch später unter „Mitglieder“ zuweisen.',
      // Plan 1a Welle 1 (D-03, W1-T02): die Zustellart nach ihrer Folge benannt.
      'Wählen Sie bei „So erfährt die Person davon“ „CLAiRIS schickt eine E-Mail mit dem Link“ oder „Nur den Link erstellen – Sie schicken ihn selbst“ und bestätigen Sie mit „Einladung senden“ bzw. „Link erstellen“.',
    ],
    hinweise: [
      // Spur nachbesserung-m3, K-1 (Punkt 5): „Widerrufen …“ gibt den reservierten Platz sofort frei. F-24 (PF-TX-firma-2): in den
      // Wörtern des Abschnitts „Einladungen“ (Unterzeile und Tooltip).
      'Die Einladung gilt 14 Tage. Eine offene Einladung mit Platz hält ihn längstens 14 Tage, bis die Person annimmt. „Widerrufen …“ gibt den Platz sofort wieder frei.',
      // Plan 1a Welle 2 (Ä-11, Ä-19 Punkt 3): bei 0 freien Plätzen die Wahl statt des gesperrten Hakens, für Konten, die
      // Plätze dazubuchen können.
      'Ist kein Platz frei und dürfen Sie Plätze dazubuchen, steht statt des Hakens die Wahl „Einen Platz dazubuchen“ oder „Ohne Platz einladen“. „Weiter zur Preisvorschau“ zeigt zuerst, was der Platz kostet; bestellt ist erst, wenn Sie dort bestätigen.',
      // Spur nachbesserung-m3, K-1 (Punkte 1 bis 3): hält eine offene Einladung einen Platz, nennen Kachel und Fenster den Weg
      // ohne Mehrkosten.
      'Ist kein Platz frei, weil offene Einladungen Plätze reservieren, sagen es die Kachel „Freie Plätze“ und das Fenster „Person einladen“, etwa „1 Platz ist für eine offene Einladung reserviert.“ Widerrufen Sie die Einladung im Abschnitt „Einladungen“, ist der Platz sofort wieder frei, ohne Mehrkosten.',
      'In der Vorführumgebung heißt die Wahl „E-Mail nur zur Ansicht vorbereiten (Vorführumgebung, nichts wird verschickt)“; es geht keine E-Mail hinaus. Im echten Betrieb schickt CLAiRIS die Einladung selbst per E-Mail.',
      'Im Abschnitt „Einladungen“ verlängern Sie mit „Erneut senden / verlängern“ (ohne E-Mail-Versand „Link verlängern“) auf 14 Tage ab heute, stellen mit „Link ersetzen“ einen neuen Link aus (der alte wird ungültig) oder ziehen die Einladung mit „Widerrufen …“ zurück; erst die Rückfrage „Einladung widerrufen?“ ändert etwas.',
      'Ein Konto, das schon zu einer anderen Firma gehört, kann nicht eingeladen werden.',
    ],
    belege: [
      ...aus(SEITEN, 'Mitglieder', 'Einladungen'),
      ...aus(ADMINISTRATION, 'Person einladen'),
      ...aus(FORMULARE, 'E-Mail-Adresse'),
      ...aus(EINLADUNG_TEXTE, 'So erfährt die Person davon', 'CLAiRIS schickt eine E-Mail mit dem Link', 'Nur den Link erstellen – Sie schicken ihn selbst',
        'Einladung senden', 'Link erstellen', 'E-Mail nur zur Ansicht vorbereiten (Vorführumgebung, nichts wird verschickt)',
        'Ohne Platz kann die Person beitreten, aber nicht recherchieren. Den Platz können Sie auch später unter „Mitglieder“ zuweisen.',
        'Eine offene Einladung mit Platz hält ihn längstens 14 Tage', '„Widerrufen …“ gibt den Platz sofort wieder frei.',
        'Einen Platz dazubuchen', 'Ohne Platz einladen', 'Weiter zur Preisvorschau', 'bestellt ist erst, wenn Sie dort bestätigen.'),
      ...aus(BEGRIFFE, 'Mitglied', 'Administrator'),
      ...aus(EINLADUNGEN, 'Erneut senden / verlängern', 'Link verlängern', 'Link ersetzen'),
      // Spur nachbesserung-m3, K-1: der Eintrag heißt „Widerrufen …“ (mitgliedTexte.ts WIDERRUFEN_MENUE); Kachel und Satz zum
      // reservierten Platz (reserviertSatz), Rückfrage in Administration.tsx.
      ...aus(MITGLIED_TEXTE, 'Widerrufen …', '1 Platz ist für eine offene Einladung reserviert.', 'Platz sofort wieder frei, ohne Mehrkosten.'),
      ...aus(ADMINISTRATION, 'Freie Plätze', 'Einladung widerrufen?'),
    ],
    fakten: [
      { datei: FORMULARE, muster: '\\{brand\\.memberLabel\\}</option>\\{adminWaehlbar && <option value=\\{MITGLIEDSCHAFTSART\\.administrator\\}>\\{brand\\.adminLabel\\}' },
      PLATZ_FELD,
      { datei: DIENST, muster: '14 \\* 86400000' },
      { datei: DIENST, muster: 'now\\(\\)\\.getTime\\(\\)\\+14\\*86400000' },
      { datei: DIENST, muster: 'ALREADY_MEMBER' },
    ],
  },
  {
    id: 'platz',
    frage: 'Lizenzplatz vergeben oder entziehen',
    bereich: 'members',
    schritte: [
      'Öffnen Sie „Mitglieder“ und klicken Sie bei der Person auf „Bearbeiten“.',
      'Setzen oder entfernen Sie den Haken beim Lizenzplatz und klicken Sie „Änderungen speichern“.',
      // Plan 1a Welle 2 (Ä-08 Punkt 1, Ä-13 Punkt 7): der kurze Weg aus der Zeile einer Person ohne Platz.
      'Hat die Person keinen Platz und ist einer frei, steht in ihrer Zeile auch „Platz geben …“; der Haken ist dann vorgewählt, und „Änderungen speichern“ weist den Platz zu.',
    ],
    hinweise: [
      // Plan 1a Welle 2 (Ä-08 Punkt 1, Ä-32 Punkt 5): die Platzspalte sagt zuerst, ob die Person recherchieren kann.
      'Recherchieren kann nur, wer einen Platz hat; verwalten geht auch ohne Platz. In der Liste steht deshalb bei jeder Person, ob sie recherchieren kann: „Kann recherchieren“ oder „Kein Platz – kann nicht recherchieren.“ Kann jemand trotz Platz gerade nicht recherchieren, steht dort der Grund, etwa „Persönliche Grenze erreicht“. Ohne Platz heißt der Zugang „Aktiv, ohne Platz“.',
      // Plan 1a Welle 2 (Ä-02 Punkt 5, V-20): der Satz unter der Platzwahl, wörtlich.
      'Wenn Sie Rolle, Zugang oder Lizenzplatz ändern: Die Person wird abgemeldet und meldet sich neu an. Wer den eigenen Platz ändert, meldet sich danach neu an.',
      'Sind alle Plätze vergeben oder durch offene Einladungen reserviert, wird die Zuweisung abgelehnt. Mehr Plätze buchen Sie unter „Tarif und Kosten“ mit „Plätze ändern …“; erlaubt der Tarif nicht mehr Plätze, wählen Sie einen größeren Tarif (siehe „Tarif wechseln“).',
    ],
    belege: [
      ...aus(SEITEN, 'Mitglieder', 'Tarif und Kosten'),
      ...aus(ADMINISTRATION, 'Bearbeiten'),
      ...aus(FORMULARE, 'Änderungen speichern'),
      ...aus(MITGLIED_TEXTE, 'Platz geben …', 'Kann recherchieren', 'Kein Platz – kann nicht recherchieren.', 'Persönliche Grenze erreicht', 'Aktiv, ohne Platz',
        'Wenn Sie Rolle, Zugang oder Lizenzplatz ändern: Die Person wird abgemeldet und meldet sich neu an. Wer den eigenen Platz ändert, meldet sich danach neu an.'),
      ...aus(BETRAEGE, 'Plätze ändern …'),
    ],
    fakten: [
      { datei: FORMULARE, muster: '\\$\\{platzEinzahl\\(brand\\.seatLabel\\)\\} zugewiesen' },
      { datei: DIENST, muster: 'Alle Lizenzplätze sind vergeben oder durch offene Einladungen reserviert\\.' },
    ],
  },
  {
    id: 'entfernen',
    frage: 'Person aus der Firma entfernen',
    bereich: 'members',
    schritte: [
      // Plan 1a Welle 1 (H-12, W1-T06): Beenden über das Zeilenmenü.
      'Öffnen Sie „Mitglieder“ und wählen Sie bei der Person unter „Weitere Aktionen“ „Mitgliedschaft beenden …“.',
      'Der Dialog „Mitgliedschaft beenden?“ nennt die Folgen; „Mitgliedschaft beenden“ bestätigt.',
    ],
    hinweise: [
      'Der Recherchezugang endet sofort. Alle Anmeldungen der Person werden beendet. Das Konto der Person bleibt bestehen, nur ohne Ihre Firma; es werden keine Kontodaten gelöscht.',
      'Verläufe, die die Person in Ihrer Firma angelegt hat, bleiben dort; die Person kann sie danach nicht mehr öffnen oder exportieren.',
      'Der Lizenzplatz wird dabei frei.',
      'Nur vorübergehend sperren statt entfernen: Wählen Sie „Pausieren …“ (siehe „Person pausieren“). Rolle, Zusatzrolle und Verläufe bleiben dabei, den Platz behalten Sie nach Wahl.',
      'Ihre eigene Mitgliedschaft beenden Sie hier nicht. Administratoren entfernt nur, wer Rollen vergeben darf. Den Besitzer entfernen Sie erst, wenn der Besitz übertragen ist. Siehe dazu „Besitz übertragen“. Die letzte Person, die die Firma verwalten darf, bleibt.',
    ],
    belege: [
      ...aus(SEITEN, 'Mitglieder'),
      ...aus(BAUSTEINE, 'Weitere Aktionen'),
      ...aus(MITGLIED_TEXTE, 'Mitgliedschaft beenden …', 'Mitgliedschaft beenden?', 'Pausieren …', 'Den Besitzer entfernen Sie erst, wenn der Besitz übertragen ist.'),
      ...aus(MITGLIED_AKTIONEN, 'Mitgliedschaft beenden'),
      ...aus(BEENDEN, 'Alle Anmeldungen der Person werden beendet.',
        'Das Konto der Person bleibt bestehen, nur ohne Ihre Firma; es werden keine Kontodaten gelöscht.',
        'die Person kann sie danach nicht mehr öffnen oder exportieren.'),
      ...aus(FIRMENDATEN, 'Besitz übertragen'),
    ],
    fakten: [
      { datei: DIENST, muster: 'organizationSeatActive: false' },
      { datei: DIENST, muster: 'SELF_PROTECTION' },
      { datei: DIENST, muster: "'roles\\.write'" },
      { datei: DIENST, muster: 'LAST_ADMIN' },
      { datei: KUNDEN, muster: 'OWNER_PROTECTED' },
    ],
  },
  {
    // Admin-Wünsche W7 (PLAN 2.7, AW-7 bis AW-9): Pause mit Wahl zum Platz, Mail an die Person, kein Enddatum. Plan 1a Welle 1
    // (MA-11, M1-16, M1-17): je Platzwahl ein Satz, keine Aussage zu „weniger zahlen“.
    id: 'pausieren',
    frage: 'Person pausieren',
    bereich: 'members',
    schritte: [
      'Öffnen Sie „Mitglieder“ und wählen Sie bei der Person unter „Weitere Aktionen“ den Eintrag „Pausieren …“.',
      'Wählen Sie „Platz behalten“ oder „Platz freigeben“ und bestätigen Sie mit „Pausieren“.',
      'Zurück holen Sie die Person mit „Fortsetzen …“. Hat sie keinen Lizenzplatz mehr, setzen Sie dort den Haken „Platz wieder zuweisen“, solange ein Platz frei ist.',
    ],
    hinweise: [
      // Plan 1a Welle 2 (vorgezogen 2, P2-16; SPEC-ADMIN.md GZ-10): das Gesperrte zuerst, das Anmelden als Folge der Abmeldung.
      'Die Person kommt ab sofort nicht mehr in die Recherche und wird auf allen Geräten abgemeldet. Nach einer neuen Anmeldung sieht sie nur ihre eigenen Einstellungen. Verläufe, Rolle und Zusatzrolle bleiben.',
      'Platz behalten: Nach der Pause kann die Person sofort weiterarbeiten. Platz freigeben: Jemand anderer kann den Platz während der Pause nutzen. Ihre Rechnung ändert sich durch die Pause nicht.',
      'Die Person bekommt beim Pausieren und beim Fortsetzen eine kurze E-Mail.',
      // Plan 1a Welle 2 (Ä-06 Punkte 2 und 3): unter „Pausiert“ steht, seit wann und von wem; die Platzspalte nennt die Pause.
      'In der Spalte „Zugang“ steht „Pausiert“, darunter seit wann und wer pausiert hat. Behält die Person den Platz, steht daneben „Platz behalten · kann während der Pause nicht recherchieren“. Über der Liste filtern „Alle“, „Aktiv“ und „Pausiert“.',
      'Sich selbst und die Besitzerin oder den Besitzer pausieren Sie nicht. Administratoren pausiert nur, wer Rollen vergeben darf; die letzte Person, die die Firma verwalten darf, bleibt aktiv.',
      'Pausieren geht auch unter einem Sperrvermerk, Entfernen nicht. Die Pause endet erst, wenn jemand „Fortsetzen …“ wählt; ein Enddatum gibt es nicht.',
    ],
    belege: [
      ...aus(SEITEN, 'Mitglieder'),
      ...aus(BAUSTEINE, 'Weitere Aktionen'),
      ...aus(MITGLIED_TEXTE, 'Pausieren …', 'Platz behalten', 'Platz freigeben', 'Fortsetzen …', 'Pausiert', 'Alle', 'Aktiv',
        'Ihre Rechnung ändert sich durch die Pause nicht.', 'Die Pause endet erst, wenn jemand „Fortsetzen …“ wählt; ein Enddatum gibt es nicht.',
        'Platz behalten · kann während der Pause nicht recherchieren', 'kommt ab sofort nicht mehr in die Recherche', 'Nach einer neuen Anmeldung sieht'),
      ...aus(MITGLIED_AKTIONEN, 'Pausieren', 'Platz wieder zuweisen'),
      ...aus(ADMINISTRATION, 'Zugang'),
    ],
    fakten: [
      { datei: DIENST, muster: "'MEMBER_PAUSED'" },
      { datei: DIENST, muster: 'organizationAccessPausedAt: now\\(\\)' },
      { datei: DIENST, muster: 'SELF_PROTECTION' },
      { datei: DIENST, muster: 'Mitglieder lassen sich bis zu dessen Ende nicht entfernen' },
      { datei: KUNDEN, muster: 'OWNER_PROTECTED' },
      { datei: MAIL, muster: 'Hinweis zu Ihrer Mitgliedschaft' },
    ],
  },
  {
    // Admin-Wünsche W6 (PLAN 2.6, F-W6-1): Hochstufen immer voll, einschränken danach über die Zusatzrolle. Plan 1a Welle 1
    // (D-08, O2-4, W1-T01): Spalte „Rolle“, Besitzerin ohne Auswahl, Rückfrage auch bei „Bearbeiten“.
    id: 'rolle',
    frage: 'Rolle ändern',
    bereich: 'roles',
    schritte: [
      // Plan 1a Welle 2 (Ä-01 Punkt 8): in der Zeile der Besitzerin oder des Besitzers steht, wer die Firma übergeben kann.
      'Öffnen Sie „Rollen“. Wählen Sie in „Personen zuordnen“ bei der Person in der Spalte „Rolle“ „Administrator“ oder „Mitglied“. Bei der Besitzerin oder dem Besitzer steht dort „Besitzer“ ohne Auswahl und darunter, wer die Firma übergeben kann.',
      'Oder öffnen Sie „Mitglieder“ und wählen Sie bei der Person unter „Weitere Aktionen“ den Eintrag zum Wechsel der Rolle.',
      // Plan 1a Welle 2 (Ä-02 Punkt 4): folgt eine Rückfrage, trägt der Knopf in „Mitglied verwalten“ die drei Punkte.
      'Der Dialog nennt die Folgen; erst die Bestätigung im Dialog ändert die Rolle. Auch „Bearbeiten“ fragt vor dem Speichern mit denselben Folgen; der Knopf dort heißt dann „Änderungen speichern …“.',
    ],
    hinweise: [
      // Plan 1a Welle 2 (Ä-02 Punkte 1 und 6): weniger erlauben geht über „Rechte anpassen“ bei der Person.
      'Ein neuer Administrator verwaltet die Firma voll: Mitglieder, Einladungen, Plätze, Rollen, Abrechnung, Rechnungen und Kontingent. Weniger erlauben Sie unter „Mitglieder“ bei der Person mit „Bearbeiten“ und „Rechte anpassen“ oder unter „Rollen“ mit einer Zusatzrolle.',
      // Plan 1a Welle 2 (Ä-03 Punkt 1): auf „Rollen“ gilt eine Auswahl erst nach „Speichern …“ und der Rückfrage.
      'Eine Zusatzrolle ordnen Sie unter „Rollen“ in der Spalte „Zusatzrolle“ zu. Die Auswahl allein ändert nichts: Sie gilt erst nach „Speichern …“ und der Bestätigung mit „Zusatzrolle zuordnen“; danach nimmt „Rückgängig“ sie zurück.',
      // Plan 1a Welle 2 (Ä-02 Punkt 6, V-21): die Rückfrage nennt die Person beim Namen.
      'Jeder Rollenwechsel meldet die Person ab; der Dialog nennt sie beim Namen: „… wird sofort auf allen Geräten abgemeldet.“',
      'Ihre eigene Rolle ändert eine andere berechtigte Person. Der Besitzer bleibt Besitzer bis zur Übergabe unter „Besitz und Nachfolge“, und die letzte Person, die die Firma verwalten darf, bleibt Administrator.',
      'Die Rolle ändert, wer „Rollen und Zuordnungen pflegen“ und „Mitgliedschaften und Plätze pflegen“ darf.',
    ],
    belege: [
      ...aus(SEITEN, 'Rollen', 'Mitglieder', 'Besitz und Nachfolge'),
      ...aus(ROLLEN, 'Personen zuordnen', 'Rolle', 'Zusatzrolle'),
      ...aus(BEGRIFFE, 'Administrator', 'Mitglied'),
      ...aus(BAUSTEINE, 'Weitere Aktionen'),
      ...aus(ADMINISTRATION, 'Bearbeiten'),
      ...aus(ROLLEN_WORT, 'Besitzer', 'Rechte anpassen'),
      ...aus(ROLLEN_SEITE_TEXTE, 'die Firma übergeben', 'Speichern …', 'Zusatzrolle zuordnen', 'Rückgängig'),
      ...aus(MITGLIED_TEXTE, 'Änderungen speichern …', 'wird sofort auf allen Geräten abgemeldet.'),
      ...aus(KATALOG, 'Rollen und Zuordnungen pflegen', 'Mitgliedschaften und Plätze pflegen'),
    ],
    fakten: [
      { datei: MITGLIED_TEXTE, muster: 'Zum \\$\\{b\\.admin\\} machen …' },
      { datei: MITGLIED_TEXTE, muster: 'Zum \\$\\{b\\.mitglied\\} machen …' },
      { datei: DIENST, muster: 'LAST_ADMIN' },
      { datei: DIENST, muster: 'Ihre eigenen Rechte erweitert eine andere berechtigte Person\\.' },
      { datei: DIENST, muster: 'tenantAdminRestrictedAt: null' },
      { datei: KUNDEN, muster: 'OWNER_PROTECTED' },
    ],
  },
  {
    // Plan 1a Welle 1 (D-09, D-10, M1-18 bis M1-22, 2.19, 2.20): Summe für die Plätze, Jahrestarif, „Größeren Tarif wählen“.
    id: 'tarif',
    frage: 'Tarif wechseln',
    bereich: 'billing',
    schritte: [
      // Admin-Wünsche W1 (PLAN 2.1, AW-1; Schritt M-4) und Plan 1a MA-01 (W-01): der kurze Weg über „Tarif und Kosten“,
      // Abschnitt „Alle Tarife“; Mitglieder und Einzelkonten behalten die Seite „Tarife“.
      // Plan 1a Welle 2 (Ä-28 Punkte 13, 16 und 16c): „Preis ansehen …“ beim Tarif, ein Name für den Schritt davor
      // („Preisvorschau erstellen“), der Hauptknopf „Alle Tarife anzeigen“ im Kopf der Seite.
      `Am schnellsten unter „Tarif und Kosten“ im Abschnitt „Alle Tarife“: Klicken Sie beim gewünschten Tarif auf „Preis ansehen …“, wählen Sie im Fenster Intervall und Plätze, klicken Sie „Preisvorschau erstellen“ und beauftragen Sie mit „Kostenpflichtig wechseln“. Oben auf der Seite springt „${MENUE.tarife}“ zu diesem Abschnitt. Mitglieder und Einzelkonten finden die Tarife unter „Tarife“.`,
      'Die Wahl nennt den Preis je Platz und die Summe für Ihre Plätze; beim Jahrestarif steht dabei, was das im Monat entspricht.',
      'Oder öffnen Sie „Rechnungen und Vertrag“ und gehen Sie zum Abschnitt „Tarif wechseln“. Wählen Sie „Neuer Tarif“ und „Intervall“, bei Bedarf einen „Rabattcode (optional)“, und klicken Sie „Preisvorschau erstellen“.',
      // Plan 1a Welle 2 (Ä-28 Punkte 1 bis 6): die Preisvorschau ist ein eigenes Fenster; bestellt wird erst mit dem Knopf unten.
      'Die Preisvorschau zeigt Tarif und Plätze und unter „Heute fällig“ den Betrag mit „Netto“, Umsatzsteuer und „Brutto“. Noch ist damit nichts bestellt: „Zahlungspflichtig beauftragen“ startet die Zahlung; im Testmodus heißt der Knopf „Testauftrag erstellen“.',
      'Hat Ihre Firma die geltende Fassung der Vertragsbedingungen noch nicht angenommen, steht unter dem Bestellknopf der Zustimmungssatz mit Links zu AGB und Datenschutzerklärung. Mit dem Klick auf den Bestellknopf nehmen Sie die Bedingungen an und bestätigen, dass Sie als Unternehmer handeln.',
    ],
    hinweise: [
      // Plan 1a Welle 2 (Ä-28 Punkte 1 und 18, Ä-12 Punkt 7): bei gleichem Tarif stehen die Plätze im Titel der Vorschau.
      'Mit der Zahlung gilt die gewählte Zahl der Plätze. Bleibt der Tarif gleich, nennt der Titel der Preisvorschau die Plätze neu und bisher; beim Tarifwechsel stehen darunter die Plätze bisher und nach der Zahlung. Mehr oder weniger Plätze im laufenden Tarif: „Plätze ändern …“. Erlaubt der Tarif nicht genug Plätze, führt „Größeren Tarif wählen“ zu „Alle Tarife“.',
      'Wartet ein Tarifwechsel noch auf die Zahlung, ist „Preisvorschau erstellen“ gesperrt, unter „Tarif und Kosten“ und „Tarife“ ebenso jedes „Preis ansehen …“; den Auftrag finden Sie unter „Offene Zahlungsaufträge“.',
      // Plan 1a Welle 2 (Ä-27 Punkt 1): der Knopf heißt „Vertrag kündigen …“; es folgt die Rückfrage mit dem letzten bezahlten Tag.
      'Kündigen: „Vertrag kündigen …“ im letzten Abschnitt „Vertrag beenden“.',
      'Den Abschnitt „Tarif wechseln“ sieht, wer „Abrechnung und Abonnement ändern“ darf.',
    ],
    belege: [
      ...aus(SEITEN, 'Rechnungen und Vertrag', 'Tarife', 'Tarif und Kosten'),
      ...aus(ABRECHNUNG, 'Tarif wechseln', 'Zahlungspflichtig beauftragen', 'nach der Zahlung', 'Offene Zahlungsaufträge',
        'Vertrag kündigen …', 'Vertrag beenden'),
      // Admin-Wünsche W1 (P-5): Formular und Angebot des Tarifwechsels stehen seit P-5 in TarifWechsel.tsx. Plan 1a G1-5:
      // der Abschnitt „Alle Tarife“ mit dem Abschluss „Kostenpflichtig wechseln“ steht in
      // administration/billing/TarifUndKosten.tsx (TarifTabelle; „Tarife“ nutzt sie ebenfalls). Plan 1a Welle 2: die Namen
      // der Knöpfe vor der Vorschau stehen in billing/vorschauTexte.ts, „Plätze ändern …“ im Baustein billing/shared.tsx,
      // „Alle Tarife anzeigen“ steht nur noch als MENUE.tarife in config/ebenen.ts (seit F-01 kein Eintrag des Kontomenüs; nur
      // dort als ganze Zeichenkette).
      ...aus(TARIF_WECHSEL, 'Neuer Tarif', 'Intervall', 'Rabattcode (optional)', 'Testauftrag erstellen'),
      ...aus(VORSCHAU_TEXTE, 'Preis ansehen …', 'Preisvorschau erstellen', 'Heute fällig', 'Größeren Tarif wählen'),
      ...aus(TARIF_UND_KOSTEN, 'Alle Tarife', 'Kostenpflichtig wechseln'),
      ...aus(EBENEN, MENUE.tarife),
      ...aus(BETRAEGE, 'Plätze ändern …'),
      // Integration Welle 2 (AA-PLAN Abschnitt 12.4): Netto, Steuer und Brutto (Paket 12 K12-12) und die Vertragsannahme (K12-7).
      ...aus(BETRAEGE, 'Netto', 'Brutto'),
      ...aus('frontend/src/config/legal.ts', 'AGB', 'Datenschutzerklärung'),
      ...aus(VERTRAG, 'als Unternehmer handeln.'),
      ...aus(KATALOG, 'Abrechnung und Abonnement ändern'),
    ],
  },
  {
    // Plan 1a Welle 1 (W1-O01, MA-13, W1-G13): „Rechnungen und Vertrag“, „Beleg ansehen“, „Musterbeleg“.
    id: 'rechnung',
    frage: 'Rechnung finden',
    bereich: 'billing',
    schritte: [
      // Plan 1a Welle 2 (Ä-29, Ä-16 Punkt 4): Jahr, Zähler und „Ausgestellt am“; unter der Nummer stehen Leistung und Zeitraum.
      'Öffnen Sie „Rechnungen und Vertrag“. Im Abschnitt „Rechnungen“ stehen alle Belege mit Nummer, Leistung und Zeitraum, dazu „Ausgestellt am“, Betrag und Status. Über der Liste wählen Sie das „Jahr“.',
      // Stripe 09 (R3-3): die Rechnung ist das PDF von Stripe, der ClaiRIS-Beleg heißt „Zahlungsbestätigung“.
      'Mit „Zahlungsbestätigung ansehen“ öffnen Sie die Zahlungsbestätigung mit Aussteller, Empfänger, Leistungen und Beträgen. Die Rechnung ist das PDF von Stripe: „Rechnung (PDF, Stripe)“ in derselben Zeile.',
      'Alle Belege eines Jahres auf einmal: Der Knopf „Alle Belege … drucken oder als PDF speichern“ nennt das gewählte Jahr und öffnet ein Fenster mit allen Belegen dieses Jahres; dort speichern oder drucken Sie mit „Drucken oder als PDF speichern“.',
    ],
    hinweise: [
      'In der Vorführumgebung heißen die Belege „Musterbeleg“; sie sind keine Rechnung, es wurde nichts abgebucht. Im echten Betrieb steht hier nach jeder Zahlung ein Beleg.',
      'Die Rechnungsanschrift ändern Sie im Abschnitt „Rechnungsprofil“, das Zahlungsmittel über „Zahlungsportal“; beides, wenn Sie „Abrechnung und Abonnement ändern“ dürfen.',
      // Spur nachbesserung-m3, G-1 (NA-16): „Firmendaten“ zeigt die Rechnungsanschrift mit dem Weg zum Rechnungsprofil.
      'Die Rechnungsanschrift steht auch unter „Firmendaten“; „Rechnungsanschrift ändern“ führt von dort zum Abschnitt „Rechnungsprofil“.',
      'Die Kündigung steht im letzten Abschnitt „Vertrag beenden“, mit Frist und Folgen vor dem Knopf.',
    ],
    belege: [
      ...aus(SEITEN, 'Rechnungen und Vertrag'),
      ...aus(ABRECHNUNG, 'Rechnungen', 'Rechnungsprofil', 'Zahlungsportal', 'Vertrag beenden'),
      ...aus(BELEG, 'Zahlungsbestätigung ansehen', 'Rechnung (PDF, Stripe)', 'Musterbeleg', 'Drucken oder als PDF speichern'),
      ...aus(ALLE_BELEGE, 'Jahr', 'Ausgestellt am', 'Alle Belege', 'drucken oder als PDF speichern'),
      ...aus(KATALOG, 'Abrechnung und Abonnement ändern'),
      ...aus(SEITEN, 'Firmendaten'),
      ...aus(RECHNUNGSANSCHRIFT_DATEI, 'Rechnungsanschrift', 'Rechnungsanschrift ändern'),
    ],
  },
  {
    // Profil-Nachbau F-08, WB-26: die Team-Nutzung geht im KI-Cockpit auf. Der Knopf des Exports heißt dort wie überall „Als
    // Tabelle herunterladen (für Excel)“ (W1-G11), nicht „Als CSV herunterladen“ wie im Bauplan (PF-508).
    // F-25 Entwurf C (PF-SL5-hilfe-1): die Übersicht zeigt Ziel und Fortschritt („Ihr Ziel“, Recherchen je 30 Tage für die ganze
    // Firma, nie je Person); Besitzer und Administratoren setzen es, jede Änderung steht im Änderungsprotokoll der Firma.
    // PF-SL5-hilfe-2: der Zeitraum gilt nicht für „Aufmerksamkeit“, dort steht immer der Stand heute (FirmenCockpit.tsx).
    id: 'team',
    frage: 'Nutzung im KI-Cockpit ansehen',
    bereich: 'usage',
    schritte: [
      'Öffnen Sie im Menü „KI-Cockpit“. „Übersicht“ zeigt die Nutzung Ihrer Firma im gewählten Zeitraum.',
      'Bei Alttarifen zeigt „Übersicht“ unter „Ihr Ziel“, wie weit Ihre Firma ist, dazu „Wer mitmacht“ und „Was als Nächstes hilft“. Das Ziel zählt Recherchen der ganzen Firma je 30 Tage, nie je Person. Besitzer und Administratoren setzen es; ohne Ziel steht „Ziel festlegen“ mit einem Beispiel.',
      '„Nutzung je Person“ zeigt Zahlen je Person, ohne Namen, solange der Besitzer Ihrer Firma sie nicht einschaltet. Weitere Teile: „Nutzung je Team“ (mit Teams), „Quellen“, „Aufmerksamkeit“.',
      'Den Zeitraum wählen Sie oben; „Aufmerksamkeit“ zeigt immer den Stand heute. Unter „Export“ öffnet „Als Tabelle herunterladen (für Excel)“ ein Fenster; mit „Ihr Passwort (Pflicht)“ speichert „Herunterladen“ die Zahlen als Tabelle.',
    ],
    hinweise: [
      'Bei Paketen zeigt das KI-Cockpit die Nutzung in Prozent, keine Recherchezahl, keine Inhalte und keine Beträge. Bei Alttarifen bleiben Anzahlen sichtbar.',
      'Jeder Aufruf von „Nutzung je Person“, jeder Export und jede Zieländerung stehen im Änderungsprotokoll.',
    ],
    belege: [
      ...aus(EBENEN, MENUE.kiCockpit),
      ...aus(COCKPIT_TEXTE, 'Übersicht', 'Ihr Ziel', 'Wer mitmacht', 'Was als Nächstes hilft', 'Ziel festlegen', 'Nutzung je Person',
        'Nutzung je Team', 'Quellen', 'Aufmerksamkeit', 'Export', 'Ihr Passwort (Pflicht)', 'Herunterladen'),
      ...aus(GELD_WOERTER, 'Als Tabelle herunterladen (für Excel)'),
    ],
    fakten: [
      { datei: COCKPIT_FIRMA, muster: 'KI_COCKPIT_EXPORTED' },
      { datei: COCKPIT_FIRMA, muster: 'KI_COCKPIT_PERSONEN_ANGESEHEN' },
      { datei: 'backend/src/administration/cockpit/ziele.ts', muster: "\\['RECHERCHEN_30_TAGE'" },
      { datei: 'backend/src/administration/cockpit/ziele.ts', muster: 'Ziele setzen Besitzer und Administratoren der Firma\\.' },
      { datei: 'backend/src/administration/cockpit/ziele.ts', muster: "'KI_COCKPIT_ZIEL_GESETZT'" },
      { datei: PROTOKOLL_KLASSEN, muster: "gruppe\\('RECHERCHE', 'MITTEL', true,[^)]*'KI_COCKPIT_ZIEL_GESETZT'" },
    ],
  },
  {
    // Profil-Nachbau F-14 (PF-810, BAU-TEAMS.md 5.4 wörtlich): die Seite „Teams“. Die Suche der Dokumente liest nur Frage,
    // Schritte und Hinweise; die Stichwörter „Abteilung“ und „Gruppe“ stehen deshalb im zweiten Hinweis.
    id: 'teams',
    frage: 'Teams anlegen und verwalten',
    bereich: 'members',
    schritte: [
      `Öffnen Sie „${EBENEN_WOERTER.adminEinstellungen}“ und dort „Teams“.`,
      'Mit „Team hinzufügen“ geben Sie zuerst den Namen ein; das Team ist danach leer.',
      'Auf der Teamseite fügen Sie Personen hinzu, nehmen sie heraus, machen eine Person zum Team-Admin und legen einen KI-Champion fest.',
    ],
    hinweise: [
      'Eine Person gehört höchstens zu einem Team.',
      'Teams fassen Personen zusammen, etwa eine Abteilung oder eine Gruppe.',
    ],
    belege: [
      ...aus(EBENEN, EBENEN_WOERTER.adminEinstellungen),
      ...aus(FIRMEN_LEISTE, 'Teams'),
      // F-24 (PF-TX-firma-3): die Unterzeile von „Teams“ heißt seitdem „Teams fassen Personen zusammen, mit …“.
      ...aus(TEAM_TEXTE, 'Team hinzufügen', 'Team-Admin', 'KI-Champion', 'Teams fassen Personen zusammen'),
    ],
    fakten: [
      { datei: TEAMS_MIGRATION, muster: 'CONSTRAINT "team_mitglieder_pkey" PRIMARY KEY \\("userId"\\)' },
    ],
  },
  {
    // PF-810 (BAU-TEAMS.md 5.4 wörtlich): das Teamkontingent sehen auch die Personen im Team („Mein Team“), daher jedes Konto.
    id: 'teamkontingent',
    frage: 'Was ist ein Teamkontingent?',
    bereich: null,
    schritte: [
      'Ein Teamkontingent ist der Teil des Kontingents Ihrer Firma, den ein Team in einem Monat höchstens verwenden darf, etwa „Arbeitsrecht: 40 % des Kontingents“.',
      'Das Kontingent der Firma gilt immer zuerst.',
      'Jede Recherche zählt genau für das Team, zu dem die Person beim Start gehört.',
    ],
    hinweise: [],
    belege: [
      ...aus(TEAM_TEXTE, 'Teamkontingent', 'des Kontingents', 'Das Kontingent der Firma gilt immer zuerst.'),
    ],
    fakten: [
      { datei: KONTINGENT_ZAEHLUNG, muster: 'teamId:team\\?\\.teamId\\?\\?null' },
    ],
  },
  {
    // PF-810 (BAU-TEAMS.md 5.4 wörtlich): den KI-Champion sehen alle im Team unter „Mein Team“.
    id: 'ki-champion',
    frage: 'Was ist ein KI-Champion?',
    bereich: null,
    schritte: [
      'Der KI-Champion ist die Ansprechperson für Fragen rund um KI und CLAiRIS in einem Team.',
      'Die Personen im Team sehen Name und E-Mail-Adresse unter „Mein Team“.',
      'Ohne eigenen KI-Champion sehen sie die KI-Verantwortliche oder den KI-Verantwortlichen der Firma.',
    ],
    hinweise: [],
    belege: [
      ...aus(TEAM_TEXTE, 'KI-Champion', 'Ansprechperson für Fragen rund um KI und CLAiRIS', 'Die Personen im Team sehen Name und E-Mail-Adresse unter „Mein Team“.',
        'die KI-Verantwortliche oder den KI-Verantwortlichen der Firma'),
      ...aus(SEITEN, 'Mein Team'),
    ],
  },
  {
    // PF-810 (BAU-TEAMS.md 5.4 wörtlich, Rechte 1.2): was ein Team-Admin im eigenen Team darf.
    id: 'team-admin',
    frage: 'Was darf ein Team-Admin?',
    bereich: null,
    schritte: [
      'Ein Team-Admin nimmt Personen ohne Team in sein Team auf, nimmt Personen heraus und legt den KI-Champion des Teams fest.',
      'Das Teamkontingent, andere Teams und das KI-Cockpit bleiben ihm verschlossen.',
    ],
    hinweise: [],
    belege: [
      ...aus(TEAM_TEXTE, 'Team-Admin', 'KI-Champion', 'Das Teamkontingent, andere Teams und das KI-Cockpit bleiben'),
    ],
  },
  {
    // Plan 1a Welle 1 (D-01, M1-01 bis M1-05): zwei Schritte, der Weg aus „Mitglieder“.
    // Plan 1a Welle 2 (Ä-01 Punkte 1 bis 4): eigener Eintrag „Besitz und Nachfolge“; „Nachfolge vorbereiten …“ öffnet ein
    // Fenster mit beiden Schritten (zuerst Administrator, dann der Besitz mit dem Passwort).
    id: 'besitz',
    frage: 'Besitz übertragen',
    bereich: 'company',
    ansicht: 'besitz',
    schritte: [
      'Öffnen Sie „Besitz und Nachfolge“ (der Eintrag steht unter „Firmendaten“), oder wählen Sie in „Mitglieder“ in Ihrer eigenen Zeile unter „Weitere Aktionen“ „Besitz übertragen …“.',
      'Wählen Sie bei „Wer soll übernehmen?“ die Person und klicken Sie „Nachfolge vorbereiten …“. Der Knopf öffnet ein Fenster mit beiden Schritten. Geändert wird erst, wenn Sie im Fenster bestätigen.',
      // Spur nachbesserung-m3, K-11 (B-elfriede H-02): Schritt 1 fragt erst in einer Zeile zurück; erst deren Knopf ändert die Rolle.
      'Schritt 1 im Fenster: „Zum Administrator machen …“. Es folgt eine Rückfrage; vorher ändert sich nichts. Erst „Zum Administrator machen“ in der Rückfrage gilt sofort; „Zurück“ ändert nichts. Ist die Person schon Administrator ohne Einschränkung, steht dort „Erledigt“.',
      'Schritt 2: „Besitz übertragen …“. Prüfen Sie die Folgen, geben Sie „Ihr Passwort“ ein und bestätigen Sie mit „Besitz übertragen“ – gleich oder später.',
    ],
    hinweise: [
      'Übertragen kann nur die Besitzerin oder der Besitzer selbst. Übernehmen kann nur, wer Administrator ohne Einschränkung ist. Hat die Person eine Einschränkung, heben Sie sie zuerst auf: unter „Mitglieder“ bei der Person „Bearbeiten“, dann „Rechte anpassen“ und „Keine Einschränkung“.',
      'Danach sind Sie Administrator; zurückgeben kann den Besitz nur, wer ihn übernommen hat. Vertrag, Zahlungsabo und Rechnungsprofil gehören der Firma und laufen unverändert weiter.',
      'Ihr eigenes Konto können Sie dann löschen; die Übertragung steht im Änderungsprotokoll.',
      'Dasselbe Fenster öffnet „Nachfolge vorbereiten …“ unter „Mitglieder“, wenn Sie bei der Person „Bearbeiten“ wählen.',
    ],
    belege: [
      ...aus(SEITEN, 'Besitz und Nachfolge', 'Firmendaten', 'Mitglieder'),
      ...aus(BESITZ_TEXTE_DATEI, 'Wer soll übernehmen?', 'Geändert wird erst, wenn Sie im Fenster bestätigen.',
        'Es folgt eine Rückfrage; vorher ändert sich nichts.', 'Zurück', 'Erledigt', 'Besitz übertragen …', 'gleich oder später.',
        'Vertrag, Zahlungsabo und Rechnungsprofil gehören der Firma und laufen unverändert weiter'),
      ...aus(ROLLEN_WORT, 'Nachfolge vorbereiten …', 'Rechte anpassen', 'Keine Einschränkung'),
      ...aus(FIRMENDATEN, 'Ihr Passwort', 'Besitz übertragen'),
      ...aus(MITGLIED_TEXTE, 'Besitz übertragen …'),
      ...aus(ADMINISTRATION, 'Bearbeiten'),
      ...aus(BEGRIFFE, 'Administrator'),
      ...aus(BAUSTEINE, 'Weitere Aktionen'),
    ],
    fakten: [
      // Der Knopf von Schritt 1 entsteht aus dem Begriff der Firma (mitgliedTexte.ts rolleKnopf); seit K-11 heißt er wie der Eintrag
      // im Zeilenmenü (rolleMenue, mit den drei Punkten), der Knopf der Rückfrage wie rolleKnopf.
      { datei: MITGLIED_TEXTE, muster: '`Zum \\$\\{b\\.admin\\} machen`' },
      { datei: MITGLIED_TEXTE, muster: 'Zum \\$\\{b\\.admin\\} machen …' },
      { datei: KUNDEN, muster: 'OWNER_FLOOR' },
      { datei: FIRMA_DIENST, muster: 'OWNER_ONLY' },
    ],
  },
  {
    // Plan 1a Welle 1 (D-15, W1-T07, M1-36, M1-42): „Sicherheit der Firma“ unter „Firmendaten“, der Weg aus „Sicherheit“.
    // Plan 1a Welle 2 (Ä-18 Punkte 1 bis 9): eigener Eintrag „Sicherheit der Firma“; die eigene Einrichtung beginnt dort mit
    // „Zuerst für mich einrichten …“; die Pflicht wirkt sofort, und das Einschalten fragt nach.
    id: 'zwei-faktor-regel',
    frage: 'Zwei-Faktor-Anmeldung für alle verlangen',
    bereich: 'company',
    ansicht: 'sicherheit',
    schritte: [
      'Öffnen Sie „Sicherheit der Firma“ (der Eintrag steht unter „Firmendaten“). Unter „Sicherheit“ führt der Verweis „Sicherheit der Firma“ direkt dorthin.',
      'Haben Sie selbst noch keine Zwei-Faktor-Anmeldung, richten Sie sie dort zuerst mit „Zuerst für mich einrichten …“ ein; erst dann können Sie sie von allen verlangen.',
      'Setzen Sie den Haken „Zwei-Faktor-Anmeldung für alle Mitglieder verlangen“, klicken Sie „Speichern …“ und bestätigen Sie mit „Von allen verlangen“.',
    ],
    hinweise: [
      'Wer sie noch nicht eingerichtet hat, richtet sie sofort ein: beim nächsten Klick oder bei der nächsten Anmeldung. Bis dahin erreicht die Person nur die Einrichtung und „Abmelden“.',
      'Wer sich über Ihr Firmenkonto anmeldet (SSO), ist ausgenommen.',
      'Ausschalten können Sie die Pflicht jederzeit.',
      'Hat jemand das Handy verloren, setzen Sie die Zwei-Faktor-Anmeldung in der Liste mit „Zurücksetzen“ zurück; die Person wird überall abgemeldet und richtet sie neu ein.',
    ],
    belege: [
      ...aus(SEITEN, 'Sicherheit', 'Firmendaten', 'Sicherheit der Firma'),
      ...aus(FIRMENDATEN, 'Sicherheit der Firma', 'Zwei-Faktor-Anmeldung für alle Mitglieder verlangen', 'Zurücksetzen'),
      ...aus(SICHERHEIT_TEXTE_DATEI, 'Zuerst für mich einrichten …', 'Speichern …', 'Von allen verlangen', 'beim nächsten Klick oder bei der nächsten Anmeldung',
        'Wer sich über Ihr Firmenkonto anmeldet (SSO), ist ausgenommen', 'Ausschalten können Sie die Pflicht jederzeit.'),
      ...aus(ZWEI_FAKTOR_TEXTE_DATEI, 'Sicherheit der Firma'),
      ...aus(EBENEN, 'Abmelden'),
    ],
    fakten: [
      { datei: PFLICHT, muster: "'/api/auth/logout'" },
      { datei: REGEL_DIENST, muster: 'TWO_FACTOR_SELF_FIRST' },
    ],
  },
  {
    id: 'zwei-faktor',
    frage: 'Zwei-Faktor-Anmeldung einrichten',
    bereich: null,
    gruppe: 'konto',
    // Plan 1a Welle 1, G3-3 (D-12, 8.06 bis 8.09): was eine Authenticator-App ist, woher sie kommt und dass erst „Bestätigen“
    // einschaltet.
    schritte: [
      // Spur nachbesserung-m3, G-2 (B-ludwig P-7): Schritt 1 sagt zuerst, was die App tut, wörtlich wie der Dialog.
      'Installieren Sie am Handy eine kostenlose App, die alle 30 Sekunden einen neuen sechsstelligen Code zeigt. Solche Apps heißen Authenticator-App, etwa Microsoft Authenticator oder Google Authenticator (App Store oder Google Play).',
      // Plan 1a Welle 2 (Ä-18 Punkt 3, GZ-17): wer die Firma verwaltet, kommt auch über „Sicherheit der Firma“ in denselben Dialog.
      'Öffnen Sie „Sicherheit“ und klicken Sie „Zwei-Faktor einrichten“; wer die Firma verwaltet, kommt auch unter „Sicherheit der Firma“ mit „Zuerst für mich einrichten …“ dorthin. Bis Sie einen Code bestätigen, ändert sich nichts.',
      // Plan 1a Welle 2 (UP-09, US-G11): dieselbe Trennung wie Schritt 2 des Dialogs (components/account/zweiFaktor.ts). Das Bild
      // zum Abfotografieren ist gebaut und ausgeschaltet (components/account/qrBild.ts); solange nennt die Hilfe es nicht.
      'Fügen Sie in der App ein Konto hinzu. Am Handy tippen Sie auf „In der Authenticator-App öffnen“. Am Computer tippen Sie den Schlüssel in der App ab.',
      'Geben Sie den „Code aus der App“ ein und klicken Sie „Bestätigen“. Erst dann ist die Zwei-Faktor-Anmeldung eingeschaltet.',
      'Speichern Sie die zehn Wiederherstellungscodes mit „Codes als Datei speichern“. Bewahren Sie die Codes getrennt vom Handy auf; jeder gilt einmal, falls das Handy fehlt.',
    ],
    hinweise: [
      'Eine Authenticator-App zeigt alle 30 Sekunden einen neuen sechsstelligen Code; sie ist kostenlos.',
      'Bei der Anmeldung folgt nach dem Passwort der Code.',
      'Das Einschalten meldet Sie auf allen anderen Geräten ab.',
      // SL-G3-4 (Bennys Antwort 09.10.2026): unter einer Firmenpflicht fragt „Ausschalten“ gleich nach dem Wiederherstellungscode
      // (ZweiFaktorAbschnitt.tsx, ohne den Umschalter); der zweite Satz sagt das.
      'Handy nicht zur Hand: Melden Sie sich mit einem Wiederherstellungscode an. Unter „Sicherheit“ erzeugen Sie dann mit „Neue Wiederherstellungscodes“ neue Codes oder schalten mit „Ausschalten“ die Zwei-Faktor-Anmeldung aus und richten sie neu ein; im Dialog wählen Sie „Wiederherstellungscode verwenden“. Verlangt Ihre Firma die Zwei-Faktor-Anmeldung, fragt der Dialog beim Ausschalten gleich nach dem Wiederherstellungscode.',
    ],
    belege: [
      ...aus(SEITEN, 'Sicherheit'),
      ...aus(ZWEI_FAKTOR_ABSCHNITT, 'Zwei-Faktor einrichten', 'Neue Wiederherstellungscodes', 'Ausschalten', 'Wiederherstellungscode verwenden'),
      ...aus(ZWEI_FAKTOR_EINRICHTEN, 'In der Authenticator-App öffnen', 'Code aus der App', 'Bestätigen'),
      ...aus(ZWEI_FAKTOR_TEXTE_DATEI, 'Codes als Datei speichern', 'Microsoft Authenticator oder Google Authenticator (App Store oder Google Play)',
        'Installieren Sie am Handy eine kostenlose App, die alle 30 Sekunden einen neuen sechsstelligen Code zeigt. Solche Apps heißen Authenticator-App',
        'Erst dann ist die Zwei-Faktor-Anmeldung eingeschaltet.', 'Bewahren Sie die Codes getrennt vom Handy auf',
        'Am Handy tippen Sie auf „In der Authenticator-App öffnen“. Am Computer tippen Sie den Schlüssel in der App ab.'),
      ...aus(SEITEN, 'Sicherheit der Firma'),
      ...aus(SICHERHEIT_TEXTE_DATEI, 'Zuerst für mich einrichten …'),
    ],
    fakten: [
      { datei: 'backend/src/utils/totp.ts', muster: 'TOTP_STEP_SECONDS = 30' },
      { datei: ZWEI_FAKTOR_DIENST, muster: 'RECOVERY_CODES = 10' },
      { datei: ZWEI_FAKTOR_DIENST, muster: 'session\\.deleteMany' },
      { datei: ZWEI_FAKTOR_DIENST, muster: 'zweiterFaktorPruefen\\(tx,user,await bestaetigt' },
    ],
  },
  {
    // Profil-Nachbau F-20 Ergänzung 3 (PF-810; BAU-VORLAGEN.md 1 und 4): die App-Seite „Vorlagen“ und „Vorlage einfügen“; jedes
    // Konto hat eigene Vorlagen, die der Firma nur mit Firma.
    id: 'vorlagen',
    frage: 'Vorlagen für Rechtsfragen',
    bereich: null,
    gruppe: 'recherche',
    schritte: [
      'Öffnen Sie in der Leiste links „Vorlagen“: Häufige Fragen einmal formulieren und mit einem Klick übernehmen.',
      'Mit „Vorlage anlegen“ geben Sie Titel, Kurzbeschreibung und Frage ein. Die Reiter heißen „Meine Vorlagen“, „Vorlagen der Firma“ (nur mit Firma) und „Beispiele“.',
      '„Verwenden“ übernimmt die Frage auf die Startseite. Auf der Startseite und in der Recherche setzt „Vorlage einfügen“ eine Vorlage direkt ins Eingabefeld.',
      'Angaben in eckigen Klammern, etwa [Branche], markiert CLAiRIS beim Übernehmen, damit Sie sie gleich überschreiben. Bitte keine Mandantendaten eintragen.',
    ],
    hinweise: [
      'Die Vorlagen der Firma pflegt die Verwaltung Ihrer Firma; lesen können sie alle in Ihrer Firma.',
      'CLAiRIS recherchiert, verfasst aber keine Schriftstücke. Eine Frage nach einem Mustertext oder einer Vorlage lehnt die Recherche ab.',
    ],
    belege: [
      // F-24 (PF-TX-hilfe-2, SPEC-hilfe-b 3.5): die kurze Unterzeile; der Satz zu eckigen Klammern (Tooltip am Feld „Frage“) und die
      // Warnung (Hinweis) stehen getrennt.
      ...aus(VORLAGEN_TEXTE, 'Vorlagen', 'Häufige Fragen einmal formulieren und mit einem Klick übernehmen', 'Vorlage anlegen',
        'Meine Vorlagen', 'Vorlagen der Firma', 'Beispiele', 'Verwenden', 'Vorlage einfügen', 'Angaben in eckigen Klammern, etwa [Branche], markiert ',
        'beim Übernehmen, damit Sie sie gleich überschreiben.', 'Bitte keine Mandantendaten eintragen.', 'Die Vorlagen der Firma pflegt die Verwaltung',
        'recherchiert, verfasst aber keine Schriftstücke. Eine Frage nach einem Mustertext oder einer Vorlage lehnt die Recherche ab.'),
    ],
    fakten: [
      { datei: RECHERCHE_WAECHTER, muster: 'Mustertext' },
    ],
  },
  {
    // Profil-Nachbau F-16 (PF-810; BAU-RECHT.md 2.1 bis 2.3): „Verträge & AVV“ der Firma; lesen mit billing.read wie „Rechnungen
    // und Vertrag“, dessen Linkzeile „Auftragsverarbeitungsvertrag (AVV)“ ebenfalls dorthin führt. Keine Rechtsauskunft.
    id: 'avv',
    frage: 'AVV annehmen',
    bereich: 'billing',
    schritte: [
      `Öffnen Sie „${EBENEN_WOERTER.adminEinstellungen}“ und dort „Verträge & AVV“. Im Abschnitt „Auftragsverarbeitung (AVV)“ steht unter „Stand“, ob Ihre Firma den AVV angenommen hat; „Als PDF herunterladen“ speichert die aktuelle Fassung.`,
      'Klicken Sie „AVV annehmen …“, setzen Sie in der Rückfrage den Haken und bestätigen Sie mit „AVV annehmen“. Die Annahme steht im Änderungsprotokoll der Firma.',
      'Oder laden Sie mit „Unterschriebenen AVV hochladen …“ den unterschriebenen AVV als PDF hoch. Der Anbieter von CLAiRIS prüft ihn und bestätigt ihn hier.',
    ],
    hinweise: [
      'Annehmen kann die Besitzerin oder der Besitzer und wer die Abrechnung ändern darf.',
      'Steht unter „Stand“ „Erforderlich für: Recherche in Ihrer Firma, ab …“, gilt: Ohne Annahme kann ab diesem Tag niemand in der Firma neu recherchieren; gespeicherte Verläufe bleiben lesbar.',
    ],
    belege: [
      ...aus(EBENEN, EBENEN_WOERTER.adminEinstellungen),
      ...aus(FIRMEN_LEISTE, 'Verträge & AVV'),
      ...aus(AVV_TEXTE, 'Auftragsverarbeitung (AVV)', 'Stand', 'Als PDF herunterladen', 'AVV annehmen …', 'AVV annehmen', 'Die Annahme steht im Änderungsprotokoll der Firma.',
        'Unterschriebenen AVV hochladen …', 'prüft ihn und bestätigt ihn hier.', 'Annehmen kann die Besitzerin oder der Besitzer und wer die Abrechnung ändern darf.',
        'Erforderlich für: Recherche in Ihrer Firma, ab ', 'Ohne Annahme kann ab diesem Tag niemand in der Firma neu recherchieren; gespeicherte Verläufe bleiben lesbar.'),
    ],
    fakten: [
      { datei: RECHERCHE_ZUGANG, muster: "'AVV_REQUIRED'" },
      { datei: RECHERCHE_ZUGANG, muster: 'gespeicherte Verläufe bleiben lesbar' },
    ],
  },
  {
    // Profil-Nachbau F-17 (PF-810; BAU-RECHT.md 3): zustimmen für jedes Konto, pflegen auf der Seite „Nutzungsrichtlinie“ der
    // Firma. Die österreichische Vorlage heißt „Entwurf“, solange sie nicht juristisch geprüft ist (F-17, FRAGEN F-R2).
    id: 'nutzungsrichtlinie',
    frage: 'Nutzungsrichtlinie zustimmen und pflegen',
    bereich: null,
    gruppe: 'konto',
    schritte: [
      'Veröffentlicht Ihre Firma eine neue Fassung, zeigt CLAiRIS sie vor dem Weiterarbeiten. Setzen Sie den Haken „Ich habe die Nutzungsrichtlinie gelesen und halte mich daran.“ und klicken Sie „Bestätigen und weiterarbeiten“.',
      `Pflegen: Öffnen Sie „${EBENEN_WOERTER.adminEinstellungen}“ und dort „Nutzungsrichtlinie“. Im Abschnitt „Text“ schreiben Sie selbst oder setzen mit „Österreichische Vorlage einsetzen“ die Vorlage ein. Gültig wird er erst mit „Fassung veröffentlichen“.`,
      'Im Abschnitt „Zustimmungen“ sehen Sie, wer bestätigt hat; „Erinnerung senden“ schickt den Offenen eine E-Mail mit der Bitte um Bestätigung.',
    ],
    hinweise: [
      'Die österreichische Vorlage trägt die Marke „Entwurf: noch nicht juristisch geprüft“, solange sie nicht juristisch geprüft ist. Die Vorlage ist ein Vorschlag von CLAiRIS und kein Rechtsrat. Bitte prüfen Sie sie und passen Sie sie an Ihre Firma an.',
      'Nach dem Veröffentlichen kann niemand in Ihrer Firma weiterarbeiten, bevor er die neue Fassung bestätigt hat. Das gilt auch für Sie.',
    ],
    belege: [
      ...aus(EBENEN, EBENEN_WOERTER.adminEinstellungen),
      ...aus(FIRMEN_LEISTE, 'Nutzungsrichtlinie'),
      ...aus(RICHTLINIE_TEXTE, 'Ich habe die Nutzungsrichtlinie gelesen und halte mich daran.', 'Bestätigen und weiterarbeiten', 'Text', 'Österreichische Vorlage einsetzen',
        'Gültig wird er erst mit „Fassung veröffentlichen“.', 'Zustimmungen', 'Erinnerung senden', 'mit der Bitte um Bestätigung.', 'Entwurf: noch nicht juristisch geprüft',
        'Die Vorlage ist ein Vorschlag von CLAiRIS und kein Rechtsrat. Bitte prüfen Sie sie und passen Sie sie an Ihre Firma an.',
        'Nach dem Veröffentlichen kann niemand in Ihrer Firma weiterarbeiten, bevor er die neue Fassung bestätigt hat. Das gilt auch für Sie.'),
    ],
    fakten: [
      { datei: ANMELDUNG_PRUEFUNG, muster: "'USAGE_POLICY_REQUIRED'" },
    ],
  },
  {
    // Profil-Nachbau F-18 (PF-810; BAU-RECHT.md 5.3): das Änderungsprotokoll der Firma mit Filtern und Detailblatt (PF-784).
    id: 'protokoll',
    frage: 'Änderungsprotokoll lesen',
    bereich: 'audit',
    schritte: [
      `Öffnen Sie „${EBENEN_WOERTER.adminEinstellungen}“ und dort „Änderungsprotokoll“. Die Tabelle nennt je Eintrag „Zeitpunkt“, „Änderung“, „Ausgeführt von“, „Ziel“, „Auswirkung“ und „Ergebnis“.`,
      'Darüber grenzen Sie die Liste ein: mit der Suche „Grund, Person oder Änderung suchen“ und den Auswahlen „Kategorie“, „Auswirkung“, „Ausgeführt von“ und „Ergebnis“. „Filter zurücksetzen“ zeigt wieder alle Einträge.',
      'Ein Klick auf die Änderung öffnet die Einzelheiten: unter „Änderungen“ je Feld den Wert vorher und nachher, dazu „Grund“ und „Ticket“, wenn sie gespeichert sind.',
      'Mit „Als Tabelle exportieren (für Excel)“ speichern Sie die Einträge Ihrer Auswahl als Tabelle.',
    ],
    hinweise: [
      '„Anbieter von CLAiRIS“ bei „Ausgeführt von“ heißt: Eine Person beim Anbieter hat die Änderung vorgenommen, etwa bei der Einrichtung oder auf Ihre Bitte. Diese Einträge tragen die Marke „Support“.',
      '„Auswirkung“ ordnet jede Änderung ein: „Niedrig“, „Mittel“, „Hoch“ oder „Kritisch“.',
    ],
    belege: [
      ...aus(EBENEN, EBENEN_WOERTER.adminEinstellungen),
      ...aus(SEITEN, 'Änderungsprotokoll'),
      ...aus(FIRMEN_PROTOKOLL, 'Zeitpunkt', 'Änderung', 'Ziel'),
      ...aus(PROTOKOLL_TEXTE, 'Ausgeführt von', 'Auswirkung', 'Ergebnis', 'Grund, Person oder Änderung suchen', 'Kategorie', 'Filter zurücksetzen', 'Änderungen',
        'Grund', 'Ticket', 'Anbieter von CLAiRIS', 'Support', 'Niedrig', 'Mittel', 'Hoch', 'Kritisch'),
      ...aus(GELD_WOERTER, 'Als Tabelle exportieren (für Excel)'),
      ...aus(PROTOKOLL, 'Eine Person beim Anbieter hat die Änderung vorgenommen, etwa bei der Einrichtung oder auf Ihre Bitte.'),
    ],
    fakten: [
      { datei: PROTOKOLL_KLASSEN, muster: "KRITISCH: 'Kritisch'" },
    ],
  },
];
